<template>
	<div v-show="!manualAddressEntry">
		<div class="input-holder wide">
			<label for="AddressSearch">Address</label>
			<input type="search" ref="addressSearch" id="AddressSearch" placeholder="Start typing an address..." @search="search" />
			<p>Can't find your address? <a class="form-link" @click="() => (manualAddressEntry = true)" href="javascript:;">Click here to enter it manually.</a></p>
		</div>
	</div>
	<div v-show="manualAddressEntry">
		<div class="d-flex flex-wrap flex-v-start flex-h-between">
			<div class="input-holder wide">
				<label for="StreetAddress">Street number and name *</label>
				<input type="text" id="StreetAddress" name="StreetAddress" v-model="model.StreetAddress" placeholder="123 Main Street" autofocus />
				<small class="p-error" v-if="form.StreetAddress.$error">{{ form.StreetAddress.$errors[0].$message }}</small>

				<p><a class="form-link" @click="() => (manualAddressEntry = false)" href="javascript:;">Cancel manual address entry and let me search for it.</a></p>
			</div>
		</div>
		<div class="d-flex flex-wrap flex-v-start flex-h-between">
			<div class="input-holder">
				<label for="Suburb">Suburb</label>
				<input type="text" id="Suburb" name="Suburb" v-model="model.Suburb" placeholder="Suburb" />
				<small class="p-error" v-if="form.Suburb.$error">{{ form.Suburb.$errors[0].$message }}</small>
			</div>

			<div class="input-holder">
				<label for="City">City *</label>
				<input type="text" id="City" name="City" v-model="model.City" placeholder="City" />
				<small class="p-error" v-if="form.City.$error">{{ form.City.$errors[0].$message }}</small>
			</div>
		</div>
		<div class="d-flex flex-wrap flex-v-start flex-h-between">
			<div class="input-holder">
				<label for="State">State</label>
				<input type="text" id="State" name="State" v-model="model.State" placeholder="State" />
				<small class="p-error" v-if="form.State.$error">{{ form.State.$errors[0].$message }}</small>
			</div>
			<div class="input-holder has-dropdown">
				<label for="Country">Country *</label>
				<div class="select-holder">
					<Select id="Country" name="Country" v-model="model.Country" :options="countryList" option-value="name" option-label="name" placeholder="Country" />
				</div>
				<small class="p-error" v-if="form.Country.$error">{{ form.Country.$errors[0].$message }}</small>
			</div>
		</div>
		<div class="input-holder">
			<label for="Postcode">Postcode *</label>
			<input type="text" id="Postcode" name="Postcode" v-model="model.Postcode" placeholder="Postcode" />
			<small class="p-error" v-if="form.Postcode.$error">{{ form.Postcode.$errors[0].$message }}</small>
		</div>
	</div>
</template>
<script lang="ts" setup>
	import { Loader } from '@googlemaps/js-api-loader';
	import useVuelidate from '@vuelidate/core';
	import { helpers, maxLength, requiredIf } from '@vuelidate/validators';
	import countries from 'i18n-iso-countries';
	import enLocale from 'i18n-iso-countries/langs/en.json';
	import { sortBy } from 'lodash';
	import { Select } from 'primevue';
	import { computed, onMounted, ref } from 'vue';
	import type { AddressModel } from './types';

	const apiKey = 'AIzaSyC_pW4uca5Z2w00gEspqLy7qhCshl6VYb8';

	countries.registerLocale(enLocale);
	const countryNames = countries.getNames('en', { select: 'official' });
	const countryList = computed(() => {
		const countries = Object.entries(countryNames).map(([code, name]) => ({ code, name }));
		// sort AU and NZ to the top
		const prioritize = ['NZ', 'AU'];
		return sortBy(
			countries,
			c => {
				const index = prioritize.indexOf(c.code);
				return index === -1 ? 99 : index;
			},
			'name'
		);
	});

	const model = defineModel<AddressModel>({ required: true });
	const props = defineProps({
		required: {
			type: Boolean,
			required: true,
		},
	});
	const manualAddressEntry = ref<boolean>(false);

	const rules = computed<Partial<Record<keyof AddressModel, unknown>>>(() => ({
		StreetAddress: {
			required: helpers.withMessage(
				'Required',
				requiredIf(() => props.required)
			),
			maxLength: helpers.withMessage('Too long', maxLength(100)),
		},
		Suburb: {
			maxLength: helpers.withMessage('Too long', maxLength(100)),
		},
		City: {
			required: helpers.withMessage(
				'Required',
				requiredIf(() => props.required)
			),
			maxLength: helpers.withMessage('Too long', maxLength(100)),
		},
		State: {
			maxLength: helpers.withMessage('Too long', maxLength(100)),
		},
		Postcode: {
			required: helpers.withMessage(
				'Required',
				requiredIf(() => props.required)
			),
			maxLength: helpers.withMessage('Too long', maxLength(20)),
		},
		Country: {
			required: helpers.withMessage(
				'Required',
				requiredIf(() => props.required)
			),
			maxLength: helpers.withMessage('Too long', maxLength(100)),
		},
	}));

	const form = useVuelidate<AddressModel>(rules, model);
	const addressSearch = ref<HTMLInputElement>(null!);

	function search(_event: Event) {
		model.value.StreetAddress = undefined;
		model.value.Suburb = undefined;
		model.value.City = undefined;
		model.value.State = undefined;
		model.value.Country = undefined;
		model.value.Postcode = undefined;
	}

	onMounted(async () => {
		if (addressSearch.value) {
			const loader = new Loader({
				apiKey: apiKey!,
				libraries: ['places'],
			});
			const places = await loader.importLibrary('places');

			const options = {
				types: ['geocode'],
				fields: ['place_id', 'formatted_address', 'address_components'],
				rankBy: google.maps.places.RankBy.DISTANCE,
			};

			const autocomplete = new places.Autocomplete(addressSearch.value!, options);

			autocomplete.addListener('place_changed', () => {
				const place = autocomplete.getPlace(),
					address = {
						StreetNumber: '',
						Street: '',
						Suburb: '',
						City: '',
						State: '',
						Country: '',
						Postcode: '',
					};

				// get the street address from the input because google will strip out letters (eg, 1A Example Street will become 1 Example Street)
				const streetAddress = addressSearch.value?.value.split(',')[0];
				if (place.address_components) {
					for (let componentIndex = 0; componentIndex < place.address_components.length; componentIndex++) {
						const item = place.address_components[componentIndex];
						for (let componentTypeIndex = 0; componentTypeIndex < item.types.length; componentTypeIndex++) {
							const type = item.types[componentTypeIndex];
							switch (type) {
								case 'street_number':
									address.StreetNumber = address.StreetNumber || item.long_name;
									break;
								case 'route':
									address.Street = address.Street || item.long_name;
									break;
								case 'sublocality':
									address.Suburb = address.Suburb || item.long_name;
									break;
								case 'locality':
									address.City = address.City || item.long_name;
									break;
								case 'political':
									address.City = address.City || item.long_name;
									break;
								case 'administrative_area_level_1':
									address.State = address.State || item.long_name;
									break;
								case 'postal_code':
									address.Postcode = address.Postcode || item.long_name;
									break;
								case 'country':
									const country = countryList.value.find(c => c.code == item.short_name);
									address.Country = address.Country || country?.name || '';
									break;
							}

							if (type == 'sublocality' && address.Suburb) {
								//break the type loop so suburb doesn't populate town or city from the political type
								break;
							}
						}
					}
				}

				model.value.StreetAddress = streetAddress;
				model.value.Suburb = address.Suburb;
				model.value.City = address.City;
				model.value.State = address.State;
				model.value.Country = address.Country;
				model.value.Postcode = address.Postcode;

				if (!address.StreetNumber || !address.Street || !address.City || !address.Country) {
					// show manual address form for validation
					manualAddressEntry.value = true;
				}
			});
		}
	});
</script>
